/**
 * Worksite map preview and turn-by-turn hand-off.
 *
 * The browser has no navigation engine, so directions are handed to the
 * platform's own map app the way the Android client hands off to an Intent.
 * No routing or ETA is computed here — the ETA shown to a worker is whatever
 * the backend already returns for the assignment.
 */

export interface WorksiteDestination {
  name?: string | null
  address?: string | null
  latitude?: number | null
  longitude?: number | null
}

export function hasCoordinates(destination: WorksiteDestination): boolean {
  return typeof destination.latitude === 'number' && typeof destination.longitude === 'number'
}

export function hasDestination(destination: WorksiteDestination): boolean {
  return hasCoordinates(destination) || Boolean(destination.address?.trim())
}

const destinationQuery = (destination: WorksiteDestination): string =>
  hasCoordinates(destination)
    ? `${destination.latitude},${destination.longitude}`
    : (destination.address || destination.name || '').trim()

/**
 * Apple Maps on Apple platforms, Google Maps elsewhere. Both are universal
 * links that resolve to the installed app when there is one and to the web map
 * otherwise, so there is no dead end on any target browser.
 */
export function directionsUrl(destination: WorksiteDestination, platform?: string): string | null {
  const query = destinationQuery(destination)
  if (!query) return null
  const userAgent = platform ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)
  const apple = /iPhone|iPad|iPod|Macintosh/i.test(userAgent)
  return apple
    ? `https://maps.apple.com/?daddr=${encodeURIComponent(query)}&dirflg=d`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`
}

export function openDirections(destination: WorksiteDestination): boolean {
  const url = directionsUrl(destination)
  if (!url || typeof window === 'undefined') return false
  window.open(url, '_blank', 'noopener,noreferrer')
  return true
}
